import { Subject } from "rxjs";
import { takeUntil, distinctUntilChanged, skip } from "rxjs/operators";
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ElementRef,
  AfterViewInit,
  QueryList,
  SimpleChanges,
  ContentChildren,
  ChangeDetectorRef,
  OnChanges,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  OnDestroy,
  AfterContentInit,
} from "@angular/core";
import { animate, style, transition, trigger, state } from '@angular/animations';
import { PhxTabComponent } from "./phx-tab.component";
import { PhxTabsetType } from "./phx-tabset.type";
import { TabsetService } from './phx-tabset.service';

@Component({
  selector: 'phx-tabset',
  templateUrl: './phx-tabset.component.html',
  styleUrls: ['./phx-tabset.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TabsetService],
  host: {
    class: 'phx-tabset',
    '[class.phx-tabset-primary]': 'phxTabType === "primary"',
    '[class.phx-tabset-secondary]': 'phxTabType === "secondary"',
  },
  animations: [
    trigger('slideAnimation', [
      state('left', style({ transform: 'translateX(0)' })),
      state('right', style({ transform: 'translateX(100%)' })),
      transition('* => *', animate(300))
    ])
  ]
})
export class PhxTabsetComponent implements AfterViewInit, AfterContentInit, OnChanges, OnDestroy {
  @Input() public phxActiveTabIndex: number = 0;
  @Input() public phxTabType: PhxTabsetType = 'secondary';
  @Input() public phxShouldMatchExactLink: boolean = false;
  @Input() public phxMatchUrl: boolean = false;
  @Output() public phxActiveTabIndexChange: EventEmitter<number> = new EventEmitter<number>();
  @ContentChildren(PhxTabComponent) public tabs: QueryList<PhxTabComponent> = new QueryList<PhxTabComponent>();
  public indicatorTransformX: string = '0px';
  public indicatorWidth: string = '0px';
  public showIndicator: boolean = false;
  private $destroy: Subject<void> = new Subject<void>();

  constructor(private _tabsetService: TabsetService, private _cdr: ChangeDetectorRef) { }

  ngAfterContentInit(): void {
    this.tabs.changes.pipe(takeUntil(this.$destroy)).subscribe(() => {
      this.syncActiveTabFromIndex();
    });
    this._tabsetService.changeIndicatorPosition$.pipe(takeUntil(this.$destroy)).subscribe(() => {
      this.checkAndUpdateIndicator();
    });
  }

  public ngOnChanges(changes: SimpleChanges) {
    const { phxActiveTabIndex, phxTabType, phxShouldMatchExactLink, phxMatchUrl } = changes;
    if (phxActiveTabIndex && !this.phxMatchUrl) {
      this.syncActiveTabFromIndex(phxActiveTabIndex.currentValue);
    }
    if (phxTabType) {
      this._tabsetService.onTabTypeChanged(phxTabType.currentValue);
    }
    if (phxShouldMatchExactLink) {
      this._tabsetService.phxShouldMatchExactLink = phxShouldMatchExactLink.currentValue;
    }
    if (phxMatchUrl) {
      this._tabsetService.phxMatchUrl = phxMatchUrl.currentValue;
    }
  }

  public ngAfterViewInit() {
    this._tabsetService.tabChanged$.pipe(
      skip(1), // skip BehaviorSubject seed
      takeUntil(this.$destroy),
      distinctUntilChanged((prev, curr) => prev?.tabBox?.nativeElement.offsetWidth == curr?.tabBox?.nativeElement.offsetWidth
        && prev?.tabBox?.nativeElement.offsetLeft == curr?.tabBox?.nativeElement.offsetLeft),
    ).subscribe(currentTab => {
      if (currentTab) {
        let index = this.tabs.toArray().indexOf(currentTab);
        if(index >= 0 && this.phxActiveTabIndex != index) {
          this.phxActiveTabIndexChange.emit(index);
          this.phxActiveTabIndex = index;
        }
        this.updateIndicatorPosition(currentTab?.tabBox);
      }
      else {
        if (this.phxActiveTabIndex !== -1) {
          this.phxActiveTabIndexChange.emit(-1);
          this.phxActiveTabIndex = -1;
        }
        this.hideIndicator();
      }
    });
    this.syncActiveTabFromIndex();
  }

  public ngOnDestroy(): void {
    this.$destroy.next();
    this.$destroy.complete();
  }

  private syncActiveTabFromIndex(index: number = this.phxActiveTabIndex): void {
    if (this.phxMatchUrl) return;
    if (index < 0 || (this.tabs?.length && index >= this.tabs.length)) {
      this.hideIndicator();
      return;
    }
    if (!this.tabs?.length) return;
    this._tabsetService.onTabChanged(this.tabs.toArray()[index]);
  }

  private _resetIndicatorPosition(): void {
    this.indicatorWidth = '0px';
    this.indicatorTransformX = 'translateX(0px)';
  }

  private hideIndicator(): void {
    this._resetIndicatorPosition();
    this.showIndicator = false;
    this._cdr.detectChanges();
  }

  private checkAndUpdateIndicator(): void {
    const activeTab: PhxTabComponent = this.tabs.find(tab => tab.isActive);
    if (activeTab) {
      this.updateIndicatorPosition(activeTab.tabBox);
    } else if (this.phxActiveTabIndex < 0) {
      this.hideIndicator();
    }
  }

  private updateIndicatorPosition(tabBox: ElementRef): void {
    const activeTabElement = tabBox?.nativeElement;
    if (!activeTabElement) return;

    // retry once if the first layout read is zero-width
    const apply = (): boolean => {
      if (activeTabElement.offsetWidth === 0) return false;
      this.indicatorWidth = `${activeTabElement.offsetWidth}px`;
      this.indicatorTransformX = `translateX(${activeTabElement.offsetLeft}px)`;
      this.showIndicator = true;
      this._cdr.detectChanges();
      return true;
    };

    requestAnimationFrame(() => {
      if (!apply()) {
        requestAnimationFrame(() => apply());
      }
    });
  }
}
